import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import * as Sentry from '@sentry/react'
import { useAuthStore } from './store/authStore'

import Landing from './pages/Landing'
import Login from './pages/Login'
import AppLayout from './components/layout/AppLayout'

import AdminDashboard        from './pages/admin/AdminDashboard'
import AdminDeliveries       from './pages/admin/AdminDeliveries'
import AdminUsers            from './pages/admin/AdminUsers'
import AdminFinances         from './pages/admin/AdminFinances'
import AdminZones            from './pages/admin/AdminZones'
import AdminAnalytics        from './pages/admin/AdminAnalytics'
import AdminSettings         from './pages/admin/AdminSettings'
import AdminExpediteurs      from './pages/admin/AdminExpediteurs'
import AdminExpediteurDetail from './pages/admin/AdminExpediteurDetail'
import AdminDeliveryDetail   from './pages/admin/AdminDeliveryDetail'
import AdminLivreurs         from './pages/admin/AdminLivreurs'
import AdminLivreurDetail    from './pages/admin/AdminLivreurDetail'
import AdminEchecsPage       from './pages/admin/AdminEchecsPage'
import AdminAccessRequests   from './pages/admin/AdminAccessRequests'

import ExpediteurDashboard        from './pages/expediteur/ExpediteurDashboard'
import ExpediteurDeliveries       from './pages/expediteur/ExpediteurDeliveries'
import ExpediteurPickup           from './pages/expediteur/ExpediteurPickup'
import ExpediteurPaiements        from './pages/expediteur/ExpediteurPaiements'
import ExpediteurClients          from './pages/expediteur/ExpediteurClients'
import ExpediteurRapports         from './pages/expediteur/ExpediteurRapports'
import { ExpediteurParcelDetailPage } from './pages/expediteur/ExpediteurParcelDetailPage'

import LivreurDashboard  from './pages/livreur/LivreurDashboard'
import LivreurColis      from './pages/livreur/LivreurColis'
import LivreurRevenus    from './pages/livreur/LivreurRevenus'
import LivreurRoute      from './pages/livreur/LivreurRoute'
import LivreurPlanning   from './pages/livreur/LivreurPlanning'
import LivreurProfil     from './pages/livreur/LivreurProfil'
import LivreurCaisse     from './pages/livreur/LivreurCaisse'
import { ParcelDetailPage } from './pages/livreur/ParcelDetailPage'

// ─── Fallback affiché en cas d'erreur non capturée dans l'arbre React ────────
function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', gap: 16,
      fontFamily: 'system-ui, sans-serif', color: '#0F2A4A',
    }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
        Une erreur inattendue s'est produite
      </h1>
      <p style={{ color: '#5B6B7E', margin: 0 }}>
        {error.message || 'Veuillez réessayer ou contacter le support.'}
      </p>
      <button
        onClick={resetError}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          background: '#4FA0E8', color: '#fff', fontWeight: 600,
          cursor: 'pointer', fontSize: '0.95rem',
        }}
      >
        Réessayer
      </button>
    </div>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        const status = error?.response?.status
        if (status && status < 500) return false
        return failureCount < 1
      },
      staleTime: 30_000,
    },
    mutations: {
      retry: false,
    },
  },
})

const ROLE_HOME: Record<string, string> = { admin: '/admin', expediteur: '/expediteur', livreur: '/livreur' }

function PrivateRoute({ children, role }: { children: React.ReactNode; role?: string }) {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (role && user?.role !== role) {
    const fallback = user ? ROLE_HOME[user.role] : '/login'
    return <Navigate to={fallback ?? '/login'} replace />
  }
  return <>{children}</>
}

function RoleRedirect() {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  return <Navigate to={ROLE_HOME[user.role]} replace />
}

export default function App() {
  return (
    // Sentry.ErrorBoundary capture toutes les erreurs React non gérées
    // et les remonte vers Sentry avec le contexte complet de la session.
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <ErrorFallback error={error as Error} resetError={resetError} />
    )}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/"      element={<Landing />} />
          <Route path="/login" element={<Login />} />

          <Route path="/dashboard" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />

          <Route path="/admin" element={<PrivateRoute role="admin"><AppLayout /></PrivateRoute>}>
            <Route index                  element={<AdminDashboard />} />
            <Route path="livraisons"           element={<AdminDeliveries />} />
            <Route path="utilisateurs"         element={<AdminUsers />} />
            <Route path="finances"             element={<AdminFinances />} />
            <Route path="zones"                element={<AdminZones />} />
            <Route path="analytics"            element={<AdminAnalytics />} />
            <Route path="parametres"           element={<AdminSettings />} />
            <Route path="expediteurs"          element={<AdminExpediteurs />} />
            <Route path="expediteurs/:id"      element={<AdminExpediteurDetail />} />
            <Route path="livreurs"             element={<AdminLivreurs />} />
            <Route path="livreurs/:id"         element={<AdminLivreurDetail />} />
            <Route path="echecs"               element={<AdminEchecsPage />} />
            <Route path="livraisons/:id"       element={<AdminDeliveryDetail />} />
            <Route path="access-requests"      element={<AdminAccessRequests />} />
          </Route>

          <Route path="/expediteur" element={<PrivateRoute role="expediteur"><AppLayout /></PrivateRoute>}>
            <Route index                 element={<ExpediteurDashboard />} />
            <Route path="livraisons"     element={<ExpediteurDeliveries />} />
            <Route path="colis/:id"      element={<ExpediteurParcelDetailPage />} />
            <Route path="pickup"         element={<ExpediteurPickup />} />
            <Route path="paiements"      element={<ExpediteurPaiements />} />
            <Route path="clients"        element={<ExpediteurClients />} />
            <Route path="rapports"       element={<ExpediteurRapports />} />
          </Route>

          <Route path="/livreur" element={<PrivateRoute role="livreur"><AppLayout /></PrivateRoute>}>
            <Route index              element={<LivreurDashboard />} />
            <Route path="colis"       element={<LivreurColis />} />
            <Route path="colis/:id"   element={<ParcelDetailPage />} />
            <Route path="revenus"     element={<LivreurRevenus />} />
            <Route path="caisse"      element={<LivreurCaisse />} />
            <Route path="route"       element={<LivreurRoute />} />
            <Route path="planning"    element={<LivreurPlanning />} />
            <Route path="profil"      element={<LivreurProfil />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}
